import React, { useState, useEffect, useMemo } from 'react';
import { feedBankAPI, StoreInventoryItem, StoreBulkBin } from '../../services/millAPI';
import MobileScrollTable from '../../components/common/MobileScrollTable';
import { agingColor, daysSince } from './bankAging';
import './Mill.css';

interface FeedBankEntry {
  id: number;
  formula_id: number;
  formula_name: string;
  feed_form: string;
  quantity_lbs: number;
  original_quantity_lbs: number;
  destination: string;
  destination_display: string;
  source_batch: string | null;
  reserved_for_order: string | null;
  notes: string;
  created_at: string;
}

interface FormulaOption {
  id: number;
  name: string;
}

interface BulkBin {
  id: number;
  name: string;
  product_name: string | null;
  feed_formula_id: number | null;
  capacity_lbs: number;
  current_lbs: number;
  space_available: number;
  percentage_full: number;
}

const MillFeedBank: React.FC = () => {
  const [entries, setEntries] = useState<FeedBankEntry[]>([]);
  const [bulkBins, setBulkBins] = useState<BulkBin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDestination, setFilterDestination] = useState<'all' | 'AVAILABLE' | 'RETAIL_STORE' | 'RESERVED' | 'USED'>('all');
  const [successMessage, setSuccessMessage] = useState('');

  // Store inventory data
  const [storeBaggedItems, setStoreBaggedItems] = useState<StoreInventoryItem[]>([]);
  const [storeBins, setStoreBins] = useState<StoreBulkBin[]>([]);
  const [storeSummary, setStoreSummary] = useState({ total_bags: 0, low_stock_bags: 0, total_bins: 0, low_bins: 0 });
  const [storeTab, setStoreTab] = useState<'bins' | 'bagged'>('bins');

  // Distribute modal
  const [showDistributeModal, setShowDistributeModal] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<FeedBankEntry | null>(null);
  const [selectedBinId, setSelectedBinId] = useState<number | ''>('');
  const [distributeQuantity, setDistributeQuantity] = useState('');
  const [distributeNotes, setDistributeNotes] = useState('');
  const [distributing, setDistributing] = useState(false);

  // Create entry modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formulas, setFormulas] = useState<FormulaOption[]>([]);
  const [newEntryFormula, setNewEntryFormula] = useState<number | ''>('');
  const [newEntryForm, setNewEntryForm] = useState('MASH');
  const [newEntryQuantity, setNewEntryQuantity] = useState('');
  const [newEntryNotes, setNewEntryNotes] = useState('');
  const [creating, setCreating] = useState(false);

  // Edit entry modal
  const [editingEntry, setEditingEntry] = useState<FeedBankEntry | null>(null);
  const [editQuantity, setEditQuantity] = useState('');
  const [editDestination, setEditDestination] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [saving, setSaving] = useState(false);

  // Delete confirmation
  const [deletingEntry, setDeletingEntry] = useState<FeedBankEntry | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Allocate to order modal
  const [allocatingEntry, setAllocatingEntry] = useState<FeedBankEntry | null>(null);
  const [matchingOrders, setMatchingOrders] = useState<any[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [allocating, setAllocating] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  // Helper to safely extract array from API response
  const extractArray = (data: any): any[] => {
    if (Array.isArray(data)) return data;
    if (data?.entries && Array.isArray(data.entries)) return data.entries;
    if (data?.results && Array.isArray(data.results)) return data.results;
    if (data?.data && Array.isArray(data.data)) return data.data;
    return [];
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load feed bank entries first — don't let store/bin failures block it
      let entriesResponse;
      try {
        entriesResponse = await feedBankAPI.getAll();
        console.log('[FeedBank] Raw API response:', entriesResponse.data);
        const parsed = extractArray(entriesResponse.data);
        console.log('[FeedBank] Parsed entries:', parsed.length);
        setEntries(parsed);
      } catch (err: any) {
        console.error('[FeedBank] Error loading entries:', err.response?.status, err.response?.data || err.message);
        setError('Failed to load feed bank entries');
      }

      // Load bulk bins and store data in parallel (non-blocking)
      try {
        const [binsResponse, storeResponse] = await Promise.all([
          feedBankAPI.getBulkBins(),
          feedBankAPI.getStoreOverview(),
        ]);
        setBulkBins(extractArray(binsResponse.data));
        const storeData = storeResponse.data;
        setStoreBaggedItems(storeData.bagged_inventory || []);
        setStoreBins(storeData.bulk_bins || []);
        setStoreSummary(storeData.summary || { total_bags: 0, low_stock_bags: 0, total_bins: 0, low_bins: 0 });
      } catch (err: any) {
        console.error('[FeedBank] Error loading bins/store:', err.response?.status, err.response?.data || err.message);
        // Don't overwrite entries error — just log it
      }
    } finally {
      setLoading(false);
    }
  };

  const showSuccess = (message: string) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleOpenDistributeModal = (entry: FeedBankEntry) => {
    setSelectedEntry(entry);
    setDistributeQuantity('');
    setDistributeNotes('');

    // Find compatible bins for this entry
    const entryFormulaId = entry.formula_id;
    const entryFormulaName = entry.formula_name?.toLowerCase() || '';
    const compatible = bulkBins.filter(bin => {
      if (bin.feed_formula_id === entryFormulaId) return true;
      if (bin.feed_formula_id != null) return false;
      if (bin.product_name) {
        return entryFormulaName.includes(bin.product_name.toLowerCase()) ||
               bin.product_name.toLowerCase().includes(entryFormulaName);
      }
      return true;
    });

    // Auto-select if exactly 1 match
    if (compatible.length === 1) {
      setSelectedBinId(compatible[0].id);
    } else {
      setSelectedBinId('');
    }

    setShowDistributeModal(true);
  };

  const getCompatibleBins = () => {
    if (!selectedEntry) return bulkBins;
    const entryFormulaId = selectedEntry.formula_id;
    const entryFormulaName = selectedEntry.formula_name?.toLowerCase() || '';
    // Filter bins: match by formula ID, or by name if bin has a product_name but no FK,
    // or truly empty (no formula AND no product_name)
    return bulkBins.filter(bin => {
      // Exact FK match
      if (bin.feed_formula_id === entryFormulaId) return true;
      // Bin has a formula assigned that doesn't match — exclude
      if (bin.feed_formula_id != null) return false;
      // Bin has no FK — check product_name as fallback
      if (bin.product_name) {
        // If bin has a name like "Dairy Goat Chow", match against entry formula name
        return entryFormulaName.includes(bin.product_name.toLowerCase()) ||
               bin.product_name.toLowerCase().includes(entryFormulaName);
      }
      // Truly empty bin (no formula, no product name) — available for any formula
      return true;
    });
  };

  const handleDistribute = async () => {
    if (!selectedEntry || !selectedBinId || !distributeQuantity) return;

    const qty = parseFloat(distributeQuantity);
    if (isNaN(qty) || qty <= 0) {
      alert('Please enter a valid quantity greater than 0');
      return;
    }

    if (qty > selectedEntry.quantity_lbs) {
      alert(`Cannot distribute more than available (${selectedEntry.quantity_lbs.toLocaleString()} lbs)`);
      return;
    }

    const selectedBin = bulkBins.find(b => b.id === selectedBinId);
    if (selectedBin && qty > selectedBin.space_available) {
      alert(`Bin only has ${selectedBin.space_available.toLocaleString()} lbs of space available`);
      return;
    }

    try {
      setDistributing(true);
      await feedBankAPI.distributeToBulkBin(selectedEntry.id, {
        bin_id: selectedBinId as number,
        quantity_lbs: qty,
        notes: distributeNotes,
      });

      showSuccess(`Distributed ${qty.toLocaleString()} lbs of ${selectedEntry.formula_name} to ${selectedBin?.name}`);
      setShowDistributeModal(false);
      setSelectedEntry(null);
      loadData(); // Reload to get updated quantities
    } catch (err: any) {
      console.error('Error distributing feed:', err);
      alert('Failed to distribute: ' + (err.response?.data?.error || err.message));
    } finally {
      setDistributing(false);
    }
  };

  const handleOpenCreateModal = async () => {
    setShowCreateModal(true);
    setNewEntryFormula('');
    setNewEntryForm('MASH');
    setNewEntryQuantity('');
    setNewEntryNotes('');
    if (formulas.length === 0) {
      try {
        const res = await feedBankAPI.getAvailable();
        // Get unique formulas from entries, or load from API
        const { data } = await import('../../services/api').then(m => m.feedFormulasAPI.getAll({ mill_only: true }));
        const list = data?.results || data || [];
        setFormulas(list.map((f: any) => ({ id: f.id, name: f.name })));
      } catch {
        // Fallback: use formulas from existing entries
        const unique = new Map<number, string>();
        entries.forEach(e => unique.set(e.formula_id, e.formula_name));
        setFormulas(Array.from(unique, ([id, name]) => ({ id, name })));
      }
    }
  };

  const handleCreateEntry = async () => {
    if (!newEntryFormula || !newEntryQuantity) return;
    const qty = parseFloat(newEntryQuantity);
    if (isNaN(qty) || qty <= 0) {
      alert('Please enter a valid quantity');
      return;
    }
    try {
      setCreating(true);
      await feedBankAPI.create({
        formula_id: newEntryFormula as number,
        feed_form: newEntryForm,
        quantity_lbs: qty,
        notes: newEntryNotes || 'Manually added',
      });
      showSuccess(`Added ${qty.toLocaleString()} lbs to Feed Bank`);
      setShowCreateModal(false);
      loadData();
    } catch (err: any) {
      alert('Failed to create entry: ' + (err.response?.data?.error || err.message));
    } finally {
      setCreating(false);
    }
  };

  // ── Edit entry ──
  const handleOpenEdit = (entry: FeedBankEntry) => {
    setEditingEntry(entry);
    setEditQuantity(String(entry.quantity_lbs));
    setEditDestination(entry.destination);
    setEditNotes(entry.notes || '');
  };

  const handleSaveEdit = async () => {
    if (!editingEntry) return;
    const qty = parseFloat(editQuantity);
    if (isNaN(qty) || qty < 0) {
      alert('Please enter a valid quantity');
      return;
    }
    try {
      setSaving(true);
      await feedBankAPI.update(editingEntry.id, {
        quantity_lbs: qty,
        destination: editDestination,
        notes: editNotes,
      });
      showSuccess('Entry updated');
      setEditingEntry(null);
      loadData();
    } catch (err: any) {
      alert('Failed to update: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  // ── Delete entry ──
  const handleConfirmDelete = async () => {
    if (!deletingEntry) return;
    try {
      setDeleting(true);
      const res = await feedBankAPI.delete(deletingEntry.id);
      showSuccess(res.data.message || 'Entry deleted');
      setDeletingEntry(null);
      loadData();
    } catch (err: any) {
      alert('Failed to delete: ' + (err.response?.data?.error || err.message));
    } finally {
      setDeleting(false);
    }
  };

  // ── Allocate to order ──
  const handleOpenAllocate = async (entry: FeedBankEntry) => {
    setAllocatingEntry(entry);
    setMatchingOrders([]);
    setLoadingOrders(true);
    try {
      const res = await feedBankAPI.getMatchingOrders(entry.id);
      setMatchingOrders(res.data.order_items || []);
    } catch (err: any) {
      alert('Failed to load matching orders: ' + (err.response?.data?.error || err.message));
      setAllocatingEntry(null);
    } finally {
      setLoadingOrders(false);
    }
  };

  const handleAllocateToOrder = async (orderItemId: number) => {
    if (!allocatingEntry) return;
    try {
      setAllocating(true);
      const res = await feedBankAPI.allocateToOrder(allocatingEntry.id, {
        order_item_id: orderItemId,
      });
      showSuccess(res.data.message);
      setAllocatingEntry(null);
      loadData();
    } catch (err: any) {
      alert('Failed to allocate: ' + (err.response?.data?.error || err.message));
    } finally {
      setAllocating(false);
    }
  };

  const filteredEntries = useMemo(() => {
    let result = [...entries];

    // Filter by search term
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      result = result.filter(e =>
        (e.formula_name || '').toLowerCase().includes(search) ||
        (e.source_batch || '').toLowerCase().includes(search)
      );
    }

    // Filter by destination
    if (filterDestination !== 'all') {
      result = result.filter(e => e.destination === filterDestination);
    }

    return result;
  }, [entries, searchTerm, filterDestination]);

  const getDestinationClass = (destination: string) => {
    switch (destination) {
      case 'AVAILABLE':
        return 'status-available';
      case 'RETAIL_STORE':
        return 'status-retail';
      case 'RESERVED':
        return 'status-reserved';
      case 'USED':
        return 'status-used';
      default:
        return '';
    }
  };

  const getDestinationLabel = (destination: string) => {
    switch (destination) {
      case 'AVAILABLE':
        return 'Available';
      case 'RETAIL_STORE':
        return 'For Retail';
      case 'RESERVED':
        return 'Reserved';
      case 'USED':
        return 'Used';
      default:
        return destination;
    }
  };

  // Summary stats
  const stats = useMemo(() => {
    const available = entries.filter(e => e.destination === 'AVAILABLE');
    const totalAvailable = available.reduce((sum, e) => sum + e.quantity_lbs, 0);
    const forRetail = entries.filter(e => e.destination === 'RETAIL_STORE');
    const totalForRetail = forRetail.reduce((sum, e) => sum + e.quantity_lbs, 0);
    const totalEntries = entries.length;
    return { totalAvailable, totalForRetail, totalEntries, availableCount: available.length };
  }, [entries]);

  if (loading) {
    return (
      <div className="mill-page">
        <div className="mill-loading">
          <div className="mill-loading-spinner"></div>
          <p>Loading feed bank...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mill-page">
      {/* Success Toast */}
      {successMessage && (
        <div className="mill-toast mill-toast-success">
          <span className="toast-icon">&#10003;</span>
          {successMessage}
        </div>
      )}

      <div className="mill-page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1>Feed Bank</h1>
          <p className="mill-page-subtitle">Manage excess feed from production batches</p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            className="mill-btn mill-btn-secondary"
            onClick={() => window.location.assign('/mill/recall')}
          >
            Recall Lookup
          </button>
          <button
            className="mill-btn mill-btn-primary"
            onClick={handleOpenCreateModal}
          >
            + Add to Feed Bank
          </button>
        </div>
      </div>

      {error && <div className="mill-alert mill-alert-error">{error}</div>}

      {/* Summary Stats */}
      <div className="inventory-stats">
        <div className="stat-card">
          <div className="stat-value">{stats.totalEntries}</div>
          <div className="stat-label">Feed Bank Entries</div>
        </div>
        <div className="stat-card success">
          <div className="stat-value">{stats.totalAvailable.toLocaleString()}</div>
          <div className="stat-label">Available (lbs)</div>
        </div>
        <div className="stat-card info">
          <div className="stat-value">{storeSummary.total_bins}</div>
          <div className="stat-label">Bulk Bins</div>
        </div>
        <div className={`stat-card ${storeSummary.low_bins > 0 ? 'warning' : ''}`}>
          <div className="stat-value">{storeSummary.low_bins}</div>
          <div className="stat-label">Low Bins</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{storeSummary.total_bags}</div>
          <div className="stat-label">Store Bags</div>
        </div>
        <div className={`stat-card ${storeSummary.low_stock_bags > 0 ? 'warning' : ''}`}>
          <div className="stat-value">{storeSummary.low_stock_bags}</div>
          <div className="stat-label">Low Stock Items</div>
        </div>
      </div>

      {/* Filters */}
      <div className="inventory-filters">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by formula or batch..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="filter-group">
          <label>Status:</label>
          <select
            value={filterDestination}
            onChange={e => setFilterDestination(e.target.value as any)}
            className="filter-select"
          >
            <option value="all">All</option>
            <option value="AVAILABLE">Available</option>
            <option value="RETAIL_STORE">For Retail</option>
            <option value="RESERVED">Reserved</option>
            <option value="USED">Used</option>
          </select>
        </div>
      </div>

      {/* Feed Bank Table */}
      <div className="inventory-table-container">
        <MobileScrollTable>
        <table className="mill-table inventory-table">
          <thead>
            <tr>
              <th>Feed Formula</th>
              <th>Form</th>
              <th>Batch</th>
              <th>Quantity</th>
              <th>Status</th>
              <th>Date Added</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredEntries.map(entry => (
              <tr key={entry.id}>
                <td className="ingredient-cell">
                  <div className="ingredient-name">{entry.formula_name}</div>
                </td>
                <td>
                  <span className={`feed-form-badge form-${(entry.feed_form || 'mash').toLowerCase()}`}>
                    {entry.feed_form}
                  </span>
                </td>
                <td className="category-cell">{entry.source_batch || 'Manual'}</td>
                <td className="quantity-cell">
                  <span className="quantity-value">
                    {entry.quantity_lbs.toLocaleString()}
                  </span>
                  <span className="quantity-unit">lbs</span>
                </td>
                <td className="status-cell">
                  <span className={`status-badge ${getDestinationClass(entry.destination)}`}>
                    {getDestinationLabel(entry.destination)}
                  </span>
                </td>
                <td className="date-cell">
                  {new Date(entry.created_at).toLocaleDateString()}
                  {entry.destination === 'AVAILABLE' && entry.quantity_lbs > 0 && (() => {
                    const d = daysSince(entry.created_at);
                    return <span style={{ marginLeft: 6, color: agingColor(d), fontWeight: 700 }}>({d}d)</span>;
                  })()}
                </td>
                <td className="actions-cell">
                  <div className="fb-actions-group">
                    {entry.destination === 'AVAILABLE' && entry.quantity_lbs > 0 && (
                      <>
                        <button
                          className="mill-btn mill-btn-sm mill-btn-primary"
                          onClick={() => handleOpenDistributeModal(entry)}
                        >
                          Distribute
                        </button>
                        <button
                          className="mill-btn mill-btn-sm mill-btn-secondary"
                          onClick={() => handleOpenAllocate(entry)}
                        >
                          Allocate
                        </button>
                      </>
                    )}
                    <button
                      className="mill-btn mill-btn-sm mill-btn-ghost"
                      onClick={() => handleOpenEdit(entry)}
                    >
                      Edit
                    </button>
                    <button
                      className="mill-btn mill-btn-sm mill-btn-danger"
                      onClick={() => setDeletingEntry(entry)}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </MobileScrollTable>

        {filteredEntries.length === 0 && (
          <div className="no-results">
            <p>No feed bank entries found.</p>
          </div>
        )}
      </div>

      {/* Store Inventory Section */}
      <div className="mill-section" style={{ marginTop: '2rem' }}>
        <h2>Store Inventory</h2>
        <p className="mill-page-subtitle">Current retail store stock levels — bins and bagged products</p>

        <div className="store-tabs">
          <button
            className={`store-tab ${storeTab === 'bins' ? 'active' : ''}`}
            onClick={() => setStoreTab('bins')}
          >
            Bulk Bins ({storeBins.length})
          </button>
          <button
            className={`store-tab ${storeTab === 'bagged' ? 'active' : ''}`}
            onClick={() => setStoreTab('bagged')}
          >
            Bagged Inventory ({storeBaggedItems.length})
          </button>
        </div>

        {/* Bulk Bins Tab */}
        {storeTab === 'bins' && (
          <div className="inventory-table-container" style={{ marginTop: '1rem' }}>
            <MobileScrollTable>
            <table className="mill-table">
              <thead>
                <tr>
                  <th>Bin Name</th>
                  <th>Product</th>
                  <th>Level</th>
                  <th>Available</th>
                  <th>Capacity</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {storeBins.map(bin => (
                  <tr key={bin.id} className={bin.needs_refill ? 'store-row-low' : ''}>
                    <td><strong>{bin.name}</strong></td>
                    <td>{bin.product_name}</td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <div className="bin-fill-bar">
                          <div
                            className="bin-fill-level"
                            style={{
                              width: `${Math.min(bin.percentage_full, 100)}%`,
                              backgroundColor: bin.percentage_full > 80 ? '#4caf50' : bin.percentage_full > 30 ? '#ff9800' : '#f44336',
                            }}
                          />
                        </div>
                        <span>{bin.current_lbs.toLocaleString()} lbs ({Math.round(bin.percentage_full)}%)</span>
                      </div>
                    </td>
                    <td>{bin.available_lbs.toLocaleString()} lbs</td>
                    <td>{bin.capacity_lbs.toLocaleString()} lbs</td>
                    <td>
                      {bin.needs_refill ? (
                        <span className="status-badge store-status-low">Needs Refill</span>
                      ) : (
                        <span className="status-badge store-status-ok">OK</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </MobileScrollTable>

            {storeBins.length === 0 && (
              <div className="no-results">
                <p>No bulk bins configured in the store.</p>
              </div>
            )}
          </div>
        )}

        {/* Bagged Inventory Tab */}
        {storeTab === 'bagged' && (
          <div className="inventory-table-container" style={{ marginTop: '1rem' }}>
            <MobileScrollTable>
            <table className="mill-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>SKU</th>
                  <th>Size</th>
                  <th>Location</th>
                  <th>On Hand</th>
                  <th>Available</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {storeBaggedItems.map(item => (
                  <tr key={item.id} className={item.needs_reorder ? 'store-row-low' : ''}>
                    <td>
                      <div className="ingredient-name">{item.product_name}</div>
                      {item.feed_formula_name && (
                        <div className="store-formula-link">{item.feed_formula_name}</div>
                      )}
                    </td>
                    <td className="category-cell">{item.sku}</td>
                    <td>{item.size_lbs} lbs</td>
                    <td>{item.location_display}</td>
                    <td className="quantity-cell">
                      <span className="quantity-value">{item.quantity}</span>
                      <span className="quantity-unit">bags</span>
                    </td>
                    <td className="quantity-cell">
                      <span className="quantity-value">{item.available_quantity}</span>
                      {item.reserved_quantity > 0 && (
                        <span className="store-reserved-note">({item.reserved_quantity} reserved)</span>
                      )}
                    </td>
                    <td>
                      {item.needs_reorder ? (
                        <span className="status-badge store-status-low">Low Stock</span>
                      ) : (
                        <span className="status-badge store-status-ok">In Stock</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </MobileScrollTable>

            {storeBaggedItems.length === 0 && (
              <div className="no-results">
                <p>No bagged inventory items in the store.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Edit Entry Modal */}
      {editingEntry && (
        <div className="mill-modal-overlay" onClick={() => setEditingEntry(null)}>
          <div className="mill-modal" onClick={e => e.stopPropagation()}>
            <div className="mill-modal-header">
              <h3>Edit Feed Bank Entry</h3>
              <button className="modal-close" onClick={() => setEditingEntry(null)}>&times;</button>
            </div>
            <div className="mill-modal-body">
              <div className="material-info">
                <h4>{editingEntry.formula_name}</h4>
                <p style={{ fontSize: '0.875rem', color: '#666' }}>
                  {editingEntry.feed_form} | Batch: {editingEntry.source_batch || 'Manual'}
                </p>
              </div>
              <div className="form-group">
                <label>Quantity (lbs)</label>
                <input
                  type="text"
                  inputMode="numeric"
                  className="form-input"
                  value={editQuantity}
                  onChange={e => setEditQuantity(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Status</label>
                <select
                  className="form-select"
                  value={editDestination}
                  onChange={e => setEditDestination(e.target.value)}
                >
                  <option value="AVAILABLE">Available</option>
                  <option value="RETAIL_STORE">For Retail</option>
                  <option value="RESERVED">Reserved</option>
                  <option value="USED">Used</option>
                </select>
              </div>
              <div className="form-group">
                <label>Notes</label>
                <textarea
                  className="form-textarea"
                  value={editNotes}
                  onChange={e => setEditNotes(e.target.value)}
                  rows={3}
                />
              </div>
            </div>
            <div className="mill-modal-footer">
              <button className="mill-btn mill-btn-secondary" onClick={() => setEditingEntry(null)}>
                Cancel
              </button>
              <button
                className="mill-btn mill-btn-primary"
                onClick={handleSaveEdit}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingEntry && (
        <div className="mill-modal-overlay" onClick={() => setDeletingEntry(null)}>
          <div className="mill-modal" onClick={e => e.stopPropagation()}>
            <div className="mill-modal-header">
              <h3>Delete Feed Bank Entry</h3>
              <button className="modal-close" onClick={() => setDeletingEntry(null)}>&times;</button>
            </div>
            <div className="mill-modal-body">
              <p>Are you sure you want to remove this Feed Bank entry? This cannot be undone.</p>
              <div className="material-info" style={{ marginTop: '1rem' }}>
                <h4>{deletingEntry.formula_name} ({deletingEntry.feed_form})</h4>
                <p>{deletingEntry.quantity_lbs.toLocaleString()} lbs | {deletingEntry.source_batch || 'Manual'}</p>
              </div>
            </div>
            <div className="mill-modal-footer">
              <button className="mill-btn mill-btn-secondary" onClick={() => setDeletingEntry(null)}>
                Cancel
              </button>
              <button
                className="mill-btn mill-btn-danger"
                onClick={handleConfirmDelete}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete Entry'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Allocate to Order Modal */}
      {allocatingEntry && (
        <div className="mill-modal-overlay" onClick={() => setAllocatingEntry(null)}>
          <div className="mill-modal mill-modal-wide" onClick={e => e.stopPropagation()}>
            <div className="mill-modal-header">
              <h3>Allocate to Order</h3>
              <button className="modal-close" onClick={() => setAllocatingEntry(null)}>&times;</button>
            </div>
            <div className="mill-modal-body">
              <div className="material-info">
                <h4>{allocatingEntry.formula_name} ({allocatingEntry.feed_form})</h4>
                <p>Available: <strong>{allocatingEntry.quantity_lbs.toLocaleString()} lbs</strong></p>
              </div>
              {loadingOrders ? (
                <p style={{ textAlign: 'center', padding: '1rem', color: '#999' }}>Loading matching orders...</p>
              ) : matchingOrders.length === 0 ? (
                <p style={{ textAlign: 'center', padding: '1rem', color: '#999' }}>
                  No pending orders found for {allocatingEntry.formula_name}.
                </p>
              ) : (
                <table className="mill-table" style={{ marginTop: '1rem' }}>
                  <thead>
                    <tr>
                      <th>Order</th>
                      <th>Customer</th>
                      <th>Delivery</th>
                      <th>Form</th>
                      <th>Needs</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {matchingOrders.map((oi: any) => (
                      <tr key={oi.id}>
                        <td><strong>#{oi.order_number}</strong></td>
                        <td>{oi.customer_name}</td>
                        <td>{oi.delivery_date ? new Date(oi.delivery_date).toLocaleDateString() : 'N/A'}</td>
                        <td>{oi.feed_form}</td>
                        <td>{oi.total_quantity_lbs.toLocaleString()} lbs</td>
                        <td>
                          <button
                            className="mill-btn mill-btn-sm mill-btn-primary"
                            onClick={() => handleAllocateToOrder(oi.id)}
                            disabled={allocating}
                          >
                            {allocating ? '...' : 'Allocate'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="mill-modal-footer">
              <button className="mill-btn mill-btn-secondary" onClick={() => setAllocatingEntry(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Entry Modal */}
      {showCreateModal && (
        <div className="mill-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="mill-modal" onClick={e => e.stopPropagation()}>
            <div className="mill-modal-header">
              <h3>Add Feed to Feed Bank</h3>
              <button className="modal-close" onClick={() => setShowCreateModal(false)}>&times;</button>
            </div>
            <div className="mill-modal-body">
              <div className="form-group">
                <label>Feed Formula</label>
                <select
                  className="form-select"
                  value={newEntryFormula}
                  onChange={e => setNewEntryFormula(e.target.value ? parseInt(e.target.value) : '')}
                >
                  <option value="">-- Select formula --</option>
                  {formulas.map(f => (
                    <option key={f.id} value={f.id}>{f.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Feed Form</label>
                <select
                  className="form-select"
                  value={newEntryForm}
                  onChange={e => setNewEntryForm(e.target.value)}
                >
                  <option value="MASH">Mash</option>
                  <option value="PELLET">Pellet</option>
                </select>
              </div>
              <div className="form-group">
                <label>Quantity (lbs)</label>
                <input
                  type="text"
                  inputMode="numeric"
                  className="form-input"
                  value={newEntryQuantity}
                  onChange={e => setNewEntryQuantity(e.target.value)}
                  placeholder="Enter amount in lbs"
                />
              </div>
              <div className="form-group">
                <label>Notes (optional)</label>
                <textarea
                  className="form-textarea"
                  value={newEntryNotes}
                  onChange={e => setNewEntryNotes(e.target.value)}
                  placeholder="e.g., Overproduction from today's run, partial pallet..."
                  rows={2}
                />
              </div>
            </div>
            <div className="mill-modal-footer">
              <button className="mill-btn mill-btn-secondary" onClick={() => setShowCreateModal(false)}>
                Cancel
              </button>
              <button
                className="mill-btn mill-btn-primary"
                onClick={handleCreateEntry}
                disabled={creating || !newEntryFormula || !newEntryQuantity}
              >
                {creating ? 'Adding...' : 'Add to Feed Bank'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Distribute Modal */}
      {showDistributeModal && selectedEntry && (
        <div className="mill-modal-overlay" onClick={() => setShowDistributeModal(false)}>
          <div className="mill-modal" onClick={e => e.stopPropagation()}>
            <div className="mill-modal-header">
              <h3>Distribute Feed to Bulk Bin</h3>
              <button className="modal-close" onClick={() => setShowDistributeModal(false)}>&times;</button>
            </div>
            <div className="mill-modal-body">
              <div className="material-info">
                <h4>{selectedEntry.formula_name}</h4>
                <p>Available: <strong>{selectedEntry.quantity_lbs.toLocaleString()} lbs</strong></p>
                <p style={{ fontSize: '0.875rem', color: '#666' }}>
                  From Batch: {selectedEntry.source_batch || 'Manual'}
                </p>
              </div>

              <div className="form-group">
                <label>Bulk Bin</label>
                {getCompatibleBins().length === 0 ? (
                  <div className="help-text" style={{ color: '#f44336', padding: '8px 0' }}>
                    No bins assigned to {selectedEntry.formula_name}. Assign a formula to a bin in Store &gt; Bulk Bins first.
                  </div>
                ) : getCompatibleBins().length === 1 ? (
                  <div style={{ padding: '8px 12px', background: '#f0f7f0', borderRadius: '6px', border: '1px solid #c8e6c9' }}>
                    <strong>{getCompatibleBins()[0].name}</strong>
                    {getCompatibleBins()[0].product_name ? ` (${getCompatibleBins()[0].product_name})` : ''}
                    <span style={{ marginLeft: '8px', color: '#666' }}>
                      {getCompatibleBins()[0].space_available.toLocaleString()} lbs available
                    </span>
                  </div>
                ) : (
                  <select
                    className="form-select"
                    value={selectedBinId}
                    onChange={e => setSelectedBinId(e.target.value ? parseInt(e.target.value) : '')}
                  >
                    <option value="">-- Select a bin --</option>
                    {getCompatibleBins().map(bin => (
                      <option key={bin.id} value={bin.id} disabled={bin.space_available <= 0}>
                        {bin.name} {bin.product_name ? `(${bin.product_name})` : ''} - {bin.space_available.toLocaleString()} lbs available
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div className="form-group">
                <label>Quantity to Distribute (lbs)</label>
                <input
                  type="text"
                  inputMode="numeric"
                  className="form-input"
                  value={distributeQuantity}
                  onChange={e => setDistributeQuantity(e.target.value)}
                  onBlur={e => {
                    const val = parseFloat(e.target.value) || 0;
                    if (val > 0) setDistributeQuantity(String(val));
                    else setDistributeQuantity('');
                  }}
                  placeholder="Enter amount in lbs"
                />
                {selectedBinId && (
                  <div className="help-text">
                    Max: {Math.min(
                      selectedEntry.quantity_lbs,
                      bulkBins.find(b => b.id === selectedBinId)?.space_available || 0
                    ).toLocaleString()} lbs
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Notes (optional)</label>
                <textarea
                  className="form-textarea"
                  value={distributeNotes}
                  onChange={e => setDistributeNotes(e.target.value)}
                  placeholder="Add any notes about this distribution..."
                  rows={2}
                />
              </div>
            </div>
            <div className="mill-modal-footer">
              <button
                className="mill-btn mill-btn-secondary"
                onClick={() => setShowDistributeModal(false)}
              >
                Cancel
              </button>
              <button
                className="mill-btn mill-btn-primary"
                onClick={handleDistribute}
                disabled={distributing || !selectedBinId || !distributeQuantity}
              >
                {distributing ? 'Distributing...' : 'Distribute Feed'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MillFeedBank;
